import { supabase } from "./supabase";

export type TypeTiersAuxiliaire = "fournisseur" | "client" | "banque" | "autre";

export interface CompteAuxiliaireRow {
  id: string;
  dossier_id: string;
  numero: string;
  racine: string;
  compteur: number;
  libelle: string;
  type_tiers: TypeTiersAuxiliaire;
  fournisseur_id: string | null;
  client_id: string | null;
  actif: boolean | null;
  fournisseurs?: { id: string; nom: string } | null;
  clients?: { id: string; nom: string } | null;
}

const SELECT_AUX_FLAT =
  "id, dossier_id, numero, racine, compteur, libelle, type_tiers, fournisseur_id, client_id, actif";

const SELECT_AUX_EMBED = `${SELECT_AUX_FLAT}, fournisseurs(id, nom), clients(id, nom)`;

async function enrichAuxiliairesWithTierNames(rows: CompteAuxiliaireRow[]): Promise<CompteAuxiliaireRow[]> {
  const fIds = [...new Set(rows.map((r) => r.fournisseur_id).filter(Boolean))] as string[];
  const cIds = [...new Set(rows.map((r) => r.client_id).filter(Boolean))] as string[];
  const [fouRes, clRes] = await Promise.all([
    fIds.length
      ? supabase.from("fournisseurs").select("id, nom").in("id", fIds)
      : Promise.resolve({ data: [] as { id: string; nom: string }[], error: null }),
    cIds.length
      ? supabase.from("clients").select("id, nom").in("id", cIds)
      : Promise.resolve({ data: [] as { id: string; nom: string }[], error: null }),
  ]);
  const fMap = new Map((fouRes.data || []).map((x) => [x.id, x]));
  const cMap = new Map((clRes.data || []).map((x) => [x.id, x]));
  return rows.map((r) => ({
    ...r,
    fournisseurs: r.fournisseur_id ? fMap.get(r.fournisseur_id) ?? null : null,
    clients: r.client_id ? cMap.get(r.client_id) ?? null : null,
  }));
}

export async function fetchComptesAuxiliaires(dossierId: string): Promise<CompteAuxiliaireRow[]> {
  const embedRes = await supabase
    .from("comptes_auxiliaires")
    .select(SELECT_AUX_EMBED)
    .eq("dossier_id", dossierId)
    .order("numero", { ascending: true });

  const q = embedRes.error
    ? await supabase
        .from("comptes_auxiliaires")
        .select(SELECT_AUX_FLAT)
        .eq("dossier_id", dossierId)
        .order("numero", { ascending: true })
    : embedRes;

  if (q.error) throw q.error;
  const raw = (q.data ?? []) as CompteAuxiliaireRow[];
  const needsEnrich = raw.some((r) => r.fournisseur_id && !r.fournisseurs) || raw.some((r) => r.client_id && !r.clients);
  if (needsEnrich) {
    return enrichAuxiliairesWithTierNames(raw.map(({ fournisseurs: _f, clients: _c, ...rest }) => rest as CompteAuxiliaireRow));
  }
  return raw;
}

/** Prochain numéro (prévisualisation) sans consommer de compteur côté séquence — recalculé comme le RPC. */
export async function previewProchainNumeroAuxiliaire(
  dossierId: string,
  racine: string
): Promise<string> {
  const { data, error } = await supabase.rpc("generer_numero_compte_auxiliaire", {
    p_dossier_id: dossierId,
    p_racine: racine,
  });
  if (error) throw error;
  return String(data ?? "").trim();
}

export async function insertCompteAuxiliaireManuel(input: {
  dossierId: string;
  racine: string;
  libelle: string;
  typeTiers: TypeTiersAuxiliaire;
}): Promise<CompteAuxiliaireRow> {
  const numero = await previewProchainNumeroAuxiliaire(input.dossierId, input.racine);
  if (!/^\d{8}$/.test(numero)) {
    throw new Error("Numéro auxiliaire invalide renvoyé par le serveur.");
  }
  const rac4 = numero.slice(0, 4);
  const compteur = parseInt(numero.slice(4), 10);
  const { data, error } = await supabase
    .from("comptes_auxiliaires")
    .insert({
      dossier_id: input.dossierId,
      numero,
      racine: rac4,
      compteur,
      libelle: input.libelle.trim(),
      type_tiers: input.typeTiers,
      fournisseur_id: null,
      client_id: null,
      compte_tresorerie_id: null,
    })
    .select(SELECT_AUX_FLAT)
    .single();
  if (error) throw error;
  const [enriched] = await enrichAuxiliairesWithTierNames([data as CompteAuxiliaireRow]);
  return enriched;
}

/** Racines courantes pour création manuelle (4 chiffres). */
export const RACINES_AUXILIAIRES_SUGGEREES: { value: string; label: string }[] = [
  { value: "4011", label: "4011 — Fournisseurs (auxiliaires)" },
  { value: "4010", label: "4010 — Fournisseurs (racine alternative)" },
  { value: "4111", label: "4111 — Clients (auxiliaires)" },
  { value: "4110", label: "4110 — Clients (racine alternative)" },
  { value: "5120", label: "5120 — Banques" },
  { value: "5310", label: "5310 — Caisse" },
];
